import { Link } from "react-router-dom";
import { Heart, Users, Baby, CalendarDays } from "lucide-react";
import { FadeUp } from "@/components/Reveal";
import { ASSETS } from "@/data/site";

const POINTS = [
  {
    icon: Baby,
    title: "For mothers — and their people",
    text: "This class was designed to teach pregnant mothers and families. The mother's partner is essential, and everyone who will surround her in the early weeks is welcomed.",
  },
  {
    icon: Heart,
    title: "Ancient wisdom, modern science",
    text: "INNATE's system of care — time-tested postpartum wisdom interwoven with modern science, supporting a woman's long-term health and vitality.",
  },
  {
    icon: CalendarDays,
    title: "Perfect timing: 20–35 weeks",
    text: "Once a week for 3–4 hours across 6 weeks — five sessions prenatally while you plan and prepare, then one gathering after birth with babies in arms.",
  },
  {
    icon: Users,
    title: "Community, fortified",
    text: "Learn alongside other pregnant families in your community, and leave with a circle that lasts far beyond the series.",
  },
];

export default function FeaturedClass() {
  return (
    <section className="bg-ivory pb-24 lg:pb-32" data-testid="featured-class-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <FadeUp>
          <div className="overflow-hidden rounded-3xl border border-espresso/15 bg-espresso text-ivory">
            <div className="grid grid-cols-1 lg:grid-cols-12">
              <div className="p-10 lg:col-span-7 lg:p-16">
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-mauve">
                  The signature class · 6 sessions · Held virtually
                </p>
                <h2 className="mt-5 font-serif text-4xl font-light leading-tight sm:text-5xl" data-testid="featured-class-title">
                  Innate Postpartum Care —<br />
                  <span className="italic text-mauve">Planning for the Fourth Trimester</span>
                </h2>
                <p className="mt-6 max-w-xl text-base leading-relaxed text-ivory/75">
                  Who else will be with this mother and her partner in the early postpartum
                  time? A grandmother? An auntie? A dear friend? All these ones are welcomed
                  and encouraged to take this class with her — at a supporter's rate of $227
                  per additional support.
                </p>
                <div className="mt-10 space-y-6">
                  {POINTS.map((p) => (
                    <div key={p.title} className="flex gap-5">
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-ivory/20 text-mauve">
                        <p.icon className="h-5 w-5" strokeWidth={1.5} />
                      </span>
                      <div>
                        <h3 className="font-serif text-xl text-ivory">{p.title}</h3>
                        <p className="mt-1 max-w-lg text-sm leading-relaxed text-ivory/60">{p.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div
                  className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-4 rounded-2xl border border-mauve/40 bg-mauve/10 p-6"
                  data-testid="featured-class-cohort-card"
                >
                  <div className="flex items-start gap-4">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-mauve/25 text-mauve">
                      <CalendarDays className="h-5 w-5" strokeWidth={1.5} />
                    </span>
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-mauve">
                        Next cohort launches
                      </p>
                      <p className="mt-1 font-serif text-2xl leading-snug text-ivory">
                        February 21, 2027
                      </p>
                    </div>
                  </div>
                  <p className="text-sm leading-relaxed text-ivory/75">
                    <span className="font-serif italic text-ivory">Sundays</span> · 3:00 – 5:00 PM · 6 weeks
                    <br />
                    <span className="text-ivory/55">Some classes may extend to 6:00 PM — seats limited to keep the circle small.</span>
                  </p>
                </div>

                <div
                  className="mt-6 flex flex-wrap items-center gap-x-8 gap-y-4 rounded-2xl border border-sienna/50 bg-sienna/15 p-6"
                  data-testid="featured-class-early-bird"
                >
                  <div className="flex items-start gap-4">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-sienna/30 font-serif text-sm italic text-ivory">
                      $50
                    </span>
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-sienna">
                        Pre-launch rate for couples
                      </p>
                      <p className="mt-1 flex items-baseline gap-3 font-serif leading-snug text-ivory">
                        <span className="text-3xl">$297</span>
                        <span className="text-sm text-ivory/50 line-through">$347</span>
                        <span className="text-sm text-ivory/70">per couple</span>
                      </p>
                    </div>
                  </div>
                  <p className="text-sm leading-relaxed text-ivory/75">
                    <span className="font-serif italic text-ivory">Book by December 31, 2026</span>
                    <br />
                    <span className="text-ivory/55">A grateful welcome to the mothers who help open this first circle.</span>
                  </p>
                </div>

                <div className="mt-10 flex flex-wrap items-center gap-6">
                  <Link
                    to="/book?service=planning-for-the-4th-trimester"
                    data-testid="featured-class-book-button"
                    className="rounded-full bg-sienna px-8 py-3.5 text-sm font-medium uppercase tracking-[0.12em] text-ivory transition-colors hover:bg-sienna-dark"
                  >
                    Reserve your seats
                  </Link>
                  <p className="text-sm text-ivory/60">
                    <span className="font-serif text-3xl text-ivory">$347</span> per couple · $227 per additional supporter
                    <br />
                    <span className="text-sienna">Pre-launch rate for couples through Dec 31, 2026 — $297 per couple.</span>
                    <br />
                    <span className="text-ivory/50">Gathered virtually — join from wherever you are</span>
                  </p>
                </div>
              </div>
              <div className="relative hidden border-l border-ivory/10 bg-[#241712] lg:col-span-5 lg:flex lg:flex-col">
                <div className="relative min-h-0 flex-1">
                  <img
                    src={ASSETS.thrive}
                    alt="Postpartum care is a necessity, not a luxury — Rachelle Garcia Seliga, CPM"
                    className="absolute inset-0 h-full w-full object-contain p-8"
                    data-testid="featured-class-photo"
                  />
                </div>
                <blockquote
                  className="shrink-0 px-10 pb-10 text-center font-serif text-sm italic leading-relaxed text-ivory/70"
                  data-testid="featured-class-training-note"
                >
                  "My postpartum practitioner training was completed through — Innate
                  Traditions / Rachelle Garcia Seliga, CPM — whose approach to honoring
                  and nurturing mothers during the postpartum season helped shape the
                  foundation of my work."
                </blockquote>
              </div>
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}
